import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  ConfirmDialog,
  EmptyState,
  LoadingState,
  Modal,
  TextAreaField,
  Toast,
} from './ui';

const TheoryQuestionsList = ({ beltId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [formData, setFormData] = useState({ question_text: '', answer_text: '' });
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const showToast = (message, tone = 'success') => setToast({ open: true, message, tone });

  const fetchQuestions = async () => {
    try {
      const response = await api.get(`/admin/belts/${beltId}/theory-questions`);
      setQuestions(response.data.data);
    } catch (error) {
      showToast('Error al cargar preguntas', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [beltId]);

  const openDialog = (item = null) => {
    setSelected(item);
    setFormData(
      item
        ? { question_text: item.question_text || '', answer_text: item.answer_text || '' }
        : { question_text: '', answer_text: '' }
    );
    setDialogOpen(true);
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question_text.trim() || !formData.answer_text.trim()) {
      showToast('La pregunta y respuesta son obligatorias', 'error');
      return;
    }
    try {
      if (selected) {
        await api.put(`/admin/belts/${beltId}/theory-questions/${selected.id}`, formData);
        showToast('Pregunta actualizada');
      } else {
        if (questions.length >= 10) {
          showToast('Límite de preguntas alcanzado (máximo 10)', 'error');
          return;
        }
        await api.post(`/admin/belts/${beltId}/theory-questions`, formData);
        showToast('Pregunta creada');
      }
      setDialogOpen(false);
      fetchQuestions();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al guardar pregunta', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/belts/${beltId}/theory-questions/${confirmId}`);
      setConfirmId(null);
      showToast('Pregunta eliminada');
      fetchQuestions();
    } catch (error) {
      showToast('Error al eliminar pregunta', 'error');
    }
  };

  const move = async (id, dir) => {
    try {
      await api.put(`/admin/belts/${beltId}/theory-questions/${id}/move-${dir}`);
      fetchQuestions();
    } catch (error) {
      showToast('Error al mover pregunta', 'error');
    }
  };

  if (loading) return <LoadingState label="Cargando preguntas…" />;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Cuestionario teórico ({questions.length}/10)</h2>
        <button type="button" className="btn-admin" onClick={() => openDialog()} disabled={questions.length >= 10}>
          Agregar
        </button>
      </div>

      {questions.length === 0 ? (
        <EmptyState title="Sin preguntas" />
      ) : (
        <ul className="space-y-3">
          {questions.map((q, index) => (
            <li key={q.id} className="rounded-lg border border-line bg-surface p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <div className="min-w-0 flex-1">
                  <span className="rounded-full bg-ink px-2 py-0.5 text-xs font-bold text-white">
                    #{q.question_number}
                  </span>
                  <p className="mt-2 font-semibold">{q.question_text}</p>
                  <p className="mt-2 border-l-4 border-primary bg-primary/5 p-2 text-sm">
                    <strong className="text-primary">Respuesta:</strong> {q.answer_text}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-1">
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={index === 0} onClick={() => move(q.id, 'up')}>↑</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={index === questions.length - 1} onClick={() => move(q.id, 'down')}>↓</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" onClick={() => openDialog(q)}>Editar</button>
                  <button type="button" className="btn-admin-ghost !min-h-9 text-danger" onClick={() => setConfirmId(q.id)}>Eliminar</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={dialogOpen}
        title={selected ? 'Editar pregunta' : 'Agregar pregunta'}
        onClose={() => setDialogOpen(false)}
        footer={
          <>
            <button type="button" className="btn-admin-secondary" onClick={() => setDialogOpen(false)}>Cancelar</button>
            <button type="submit" form="theory-form" className="btn-admin">{selected ? 'Actualizar' : 'Crear'}</button>
          </>
        }
      >
        <form id="theory-form" className="space-y-4" onSubmit={handleSubmit}>
          <TextAreaField id="question_text" label="Pregunta" value={formData.question_text} onChange={handleChange} required rows={3} />
          <TextAreaField id="answer_text" label="Respuesta" value={formData.answer_text} onChange={handleChange} required rows={3} />
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Eliminar pregunta"
        message="¿Estás seguro de eliminar esta pregunta?"
        confirmLabel="Eliminar"
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmId(null)}
      />

      <Toast open={toast.open} message={toast.message} tone={toast.tone} onClose={() => setToast((t) => ({ ...t, open: false }))} />
    </div>
  );
};

export default TheoryQuestionsList;
